// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { BendTypeName } from '@evolvemep/api-ts';
import { Flex, SegmentedControl, Stack, Text } from '@mantine/core';
import { useResizeObserver } from '@mantine/hooks';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { ToggleButton } from 'components/Mantine/ToggleButton';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { BendLayoutGeometry } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendGeometries/BendLayoutGeometry';
import type { BendPreviewSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import {
  BendPreviewRenderer,
  type RenderMode,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewRenderer';
import { getBendPreview } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/getBendPreview';
import { useBendPreviewContext } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';

export type BendQuickViewProps = {
  /** When true, hides the layout button even when the bend preview data is available. */
  hideLayoutButton?: boolean;
  bendType: BendTypeName;
  bendPreviewData: BendPreviewSpecifications | null;
  /** @defaultValue '3d' */
  initialPreviewMode?: RenderMode;
};

type QuickViewRenderAreaProps = BendQuickViewProps & {
  renderMode: RenderMode;
  /**
   * When true, shows the bend labels.
   * @defaultValue true
   */
  showLabels?: boolean;
  /**
   * When true, shows the bend layout (flat tube with marks) instead of bent shape.
   * @defaultValue false
   */
  showLayout?: boolean;
};

const QuickViewRenderArea = ({
  bendType,
  bendPreviewData,
  renderMode,
  showLabels = true,
  showLayout = false,
}: QuickViewRenderAreaProps) => {
  const { highlightedObject, setHighlightedObject } = useBendPreviewContext();

  const renderer = useRef<BendPreviewRenderer | null>(null);
  // Set the animation loop.
  useEffect(() => renderer.current?.animate(renderMode), [renderMode]);

  // Update the highlighted object when the context changes
  useEffect(() => {
    renderer.current?.updateHighlightedObject(highlightedObject);
  }, [highlightedObject]);

  // Update geometry when input props change
  useEffect(() => {
    if (isNil(renderer.current) || isNil(bendType)) return undefined;
    const bendGeometry = showLayout ? new BendLayoutGeometry() : getBendPreview(bendType);
    bendGeometry.createBendPreview(bendPreviewData);
    renderer.current.setGeometry(bendGeometry);
    return () => {
      renderer.current?.clearGeometry();
      bendGeometry.dispose();
    };
  }, [bendPreviewData, bendType, showLayout]);

  useEffect(() => {
    renderer.current?.setLabelsVisible(showLabels);
  }, [showLabels]);

  // Clean up the renderer on unmount
  useEffect(
    () => () => {
      renderer.current?.dispose();
      renderer.current = null;
    },
    [],
  );

  const [ref, rect] = useResizeObserver();

  // Resize logic
  useEffect(() => {
    renderer.current?.updateSize(rect.width, rect.height);
  }, [rect.width, rect.height]);

  return (
    <>
      <Flex flex={1} align="center" justify="center" style={{ overflow: 'hidden', cursor: 'crosshair' }} ref={ref}>
        <canvas
          style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
          ref={(canvas) => {
            if (isNil(canvas) || isNotNil(renderer.current)) return;
            renderer.current = new BendPreviewRenderer(canvas, setHighlightedObject);
            const { width, height } = canvas.getBoundingClientRect();
            renderer.current.updateSize(width, height);
          }}
        />
      </Flex>
      {isNil(bendPreviewData) && (
        <Text size="xs" c="dimmed" fs="italic" pos="absolute" bottom={0} left={0} maw={500}>
          This is a preview of the bend type. A preview using your specified dimensions will be generated once you have
          entered valid values.
        </Text>
      )}
    </>
  );
};

type ViewMode = RenderMode | 'layout';

const viewOptions: { label: string; icon: EvolveIconName; value: ViewMode }[] = [
  { label: '3D Preview', icon: 'PerspectiveView', value: '3d' },
  { label: '2D Front view', icon: 'FrontView', value: '2dFront' },
  { label: '2D Top view', icon: 'TopView', value: '2dTop' },
  { label: 'Layout view', icon: 'LayoutMarkings', value: 'layout' },
];

type BendPreviewControlsProps = {
  previewMode: ViewMode;
  onChangePreviewMode: (value: ViewMode) => void;
  showLabels: boolean;
  toggleLabels: () => void;
  disableLabelToggle: boolean;
  showLayoutControls: boolean;
};

const QuickViewControls = ({
  previewMode,
  onChangePreviewMode,
  showLabels,
  toggleLabels,
  showLayoutControls,
  disableLabelToggle,
}: BendPreviewControlsProps) => {
  const availableViewOptions = useMemo(
    () => (showLayoutControls ? viewOptions : viewOptions.filter((option) => option.value !== 'layout')),
    [showLayoutControls],
  );
  return (
    <Stack gap="xs" pos="absolute" top={0} right={0} align="center" justify="center">
      <SegmentedControl
        orientation="vertical"
        value={previewMode}
        onChange={onChangePreviewMode}
        data={availableViewOptions.map(({ label, icon, value }) => ({
          label: (
            <AgGridStyleTooltip label={label} withArrow>
              <Flex align="center" justify="center" miw={24} mih={38}>
                <EvolveIcon icon={icon} />
              </Flex>
            </AgGridStyleTooltip>
          ),
          value,
        }))}
      />
      <ToggleButton
        checked={showLabels}
        onToggle={toggleLabels}
        disabled={disableLabelToggle}
        actionLabelWhenChecked="Hide labels"
        actionLabelWhenUnchecked="Show labels"
        icon="Label"
        size="xl"
      />
    </Stack>
  );
};

export const BendQuickView = (props: BendQuickViewProps) => {
  const [previewMode, setPreviewMode] = useState<ViewMode>(props.initialPreviewMode ?? '3d');
  const [showLabels, setShowLabels] = useState(true);

  const isLayout = previewMode === 'layout';
  const layoutAvailable = isNotNil(props.bendPreviewData?.layoutDimensions) && !props.hideLayoutButton;
  // "layout" is not actually a render mode. We use the 2D front view in that case.
  // We also fall back to this if the layout mode was selected but became unavailable.
  const renderMode = isLayout ? '2dFront' : previewMode;

  return (
    <>
      <QuickViewControls
        previewMode={isLayout && !layoutAvailable ? renderMode : previewMode}
        onChangePreviewMode={(newMode: ViewMode) => {
          setPreviewMode(newMode);
          if (newMode === 'layout') {
            setShowLabels(true);
          }
        }}
        showLabels={showLabels}
        toggleLabels={() => setShowLabels((v) => !v)}
        showLayoutControls={layoutAvailable}
        disableLabelToggle={isLayout && layoutAvailable}
      />
      <QuickViewRenderArea
        {...props}
        renderMode={renderMode}
        showLabels={showLabels}
        showLayout={isLayout && layoutAvailable}
      />
    </>
  );
};
